import { Link } from 'react-router'
import {
  UserPlus, CalendarPlus, ClipboardCheck, Receipt,
  UserSearch, Target, FileText, BarChart3, Zap,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { ChartCard } from '@/components/common'
import { useAuthStore } from '@/stores/authStore'
import { cn } from '@/lib/utils'

interface QuickAction {
  label: string
  to: string
  icon: LucideIcon
  tint: string
  permission?: string
}

/** The 4x2 shortcut grid, in the order shown in the design. */
const ACTIONS: QuickAction[] = [
  { label: 'Add Employee', to: '/employees?new=1', icon: UserPlus,       tint: 'bg-[var(--color-tint-blue)] text-[var(--color-info)]',        permission: 'employee:write' },
  { label: 'Apply Leave',  to: '/leave?new=1',     icon: CalendarPlus,   tint: 'bg-[var(--color-tint-red)] text-[var(--color-danger)]',       permission: 'leave:write' },
  { label: 'Attendance',   to: '/attendance',      icon: ClipboardCheck, tint: 'bg-[var(--color-tint-green)] text-[var(--color-success)]',    permission: 'attendance:read' },
  { label: 'Payslip',      to: '/payroll',         icon: Receipt,        tint: 'bg-[var(--color-primary-soft)] text-[var(--color-primary)]',  permission: 'payroll:read' },
  { label: 'Recruitment',  to: '/recruitment',     icon: UserSearch,     tint: 'bg-[var(--color-tint-red)] text-[var(--color-danger)]',       permission: 'recruitment:read' },
  { label: 'Performance',  to: '/performance',     icon: Target,         tint: 'bg-[var(--color-tint-orange)] text-[var(--color-warning)]',   permission: 'performance:read' },
  { label: 'Documents',    to: '/documents',       icon: FileText,       tint: 'bg-[var(--color-tint-blue)] text-[var(--color-info)]',        permission: 'document:read' },
  { label: 'Reports',      to: '/reports',         icon: BarChart3,      tint: 'bg-[var(--color-tint-green)] text-[var(--color-success)]',    permission: 'report:read' },
]

/**
 * Shortcuts to the eight most common tasks.
 *
 * Filtered by permission, so the grid never offers a route the user would be bounced from.
 */
export function QuickAccessCard() {
  const permissions = useAuthStore((state) => state.permissions)
  const isSuperAdmin = useAuthStore((state) => state.user?.roles.includes('SUPER_ADMIN') ?? false)

  const visible = ACTIONS.filter(
    (action) => isSuperAdmin || !action.permission || permissions.has(action.permission),
  )

  return (
    <ChartCard title="Quick Access" icon={Zap}>
      <div className="grid grid-cols-4 gap-x-2 gap-y-4 py-2">
        {visible.map((action) => {
          const Icon = action.icon
          return (
            <Link
              key={action.label}
              to={action.to}
              className="group flex min-w-0 flex-col items-center gap-1.5 rounded-[var(--radius-control)] p-1 text-center focus-visible:outline-2 focus-visible:outline-[var(--color-primary)]"
            >
              <span
                className={cn(
                  'grid size-11 place-items-center rounded-xl transition-transform group-hover:scale-105',
                  action.tint,
                )}
              >
                <Icon className="size-5" aria-hidden="true" />
              </span>
              {/* Wraps to two lines rather than overflowing into the neighbouring cell. */}
              <span className="w-full text-[11px] leading-tight text-balance text-[var(--color-body)]">
                {action.label}
              </span>
            </Link>
          )
        })}
      </div>
    </ChartCard>
  )
}
